import { useEffect, useState } from "react";
import { Building2, CirclePlus, MapPin, Pencil, Power, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";
import { MovementDetailSheet } from "@/components/MovementDetailSheet";
import { WarehouseDetailSheet } from "@/components/WarehouseDetailSheet";

const warehouseTypes = [
  ["principal", "Dépôt principal"],
  ["zone_conakry", "Dépôt Zone Conakry"],
  ["zone_interieur", "Dépôt Zone Intérieur"],
  ["livreur", "Dépôt Livreur"],
];

const emptyWarehouse = {
  name: "",
  zone_id: "",
  subzone: "",
  warehouse_type: "zone_conakry",
  transfers_enabled: false,
  delivery_user_id: "",
  responsible_user_id: "",
};

export default function LocationsPage({ user }) {
  const [zones, setZones] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [responsibleSearch, setResponsibleSearch] = useState("");
  const [responsibleRole, setResponsibleRole] = useState("");
  const [zone, setZone] = useState({ name: "", subzones: "" });
  const [warehouse, setWarehouse] = useState(emptyWarehouse);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [subzoneFilter, setSubzoneFilter] = useState("");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [selectedMovementId, setSelectedMovementId] = useState("");
  const canAssignResponsible = user?.role === "admin_principal";

  const load = async () => {
    try {
      const [zoneResponse, warehouseResponse] = await Promise.all([
        api.get("/zones"),
        api.get("/warehouses", { params: { include_inactive: true } }),
      ]);
      setZones(zoneResponse.data);
      setWarehouses(warehouseResponse.data);
      try {
        const userResponse = await api.get("/auth/users");
        setUsers(userResponse.data.filter((user) => user.role === "livreur" && user.active));
      } catch {
        setUsers([]);
      }
      setError("");
    } catch (requestError) {
      setError(getError(requestError));
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!canAssignResponsible) {
      setResponsibles([]);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      api.get("/warehouses/responsibles", {
        params: {
          search: responsibleSearch.trim(),
          role: responsibleRole,
        },
      })
        .then((response) => setResponsibles(response.data))
        .catch((requestError) => toast.error(getError(requestError)));
    }, 160);

    return () => window.clearTimeout(timer);
  }, [canAssignResponsible, responsibleRole, responsibleSearch]);

  useEffect(() => {
    const openWarehouseDetail = (event) => {
      const warehouseRow = event.target.closest('[data-testid^="warehouse-item-"]');

      if (!warehouseRow || event.target.closest("button, input, select")) {
        return;
      }

      setSelectedWarehouseId(warehouseRow.dataset.testid.replace("warehouse-item-", ""));
    };

    document.addEventListener("click", openWarehouseDetail);

    return () => document.removeEventListener("click", openWarehouseDetail);
  }, []);

  const addZone = async (event) => {
    event.preventDefault();
    try {
      await api.post("/zones", {
        name: zone.name,
        subzones: zone.subzones.split(",").map((item) => item.trim()).filter(Boolean),
      });
      setZone({ name: "", subzones: "" });
      toast.success("Zone créée");
      load();
    } catch (requestError) {
      toast.error(getError(requestError));
    }
  };

  const saveWarehouse = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        ...warehouse,
        delivery_user_id: warehouse.delivery_user_id || null,
        responsible_user_id: warehouse.responsible_user_id || null,
      };
      if (editing) {
        await api.put(`/warehouses/${editing.warehouse_id}`, payload);
        toast.success("Dépôt mis à jour");
      } else {
        await api.post("/warehouses", payload);
        toast.success("Dépôt créé");
      }
      setWarehouse(emptyWarehouse);
      setEditing(null);
      load();
    } catch (requestError) {
      toast.error(getError(requestError));
    }
  };

  const startEdit = (item) => {
    setEditing(item);
    setResponsibleSearch("");
    setResponsibleRole("");
    setWarehouse({
      name: item.name,
      zone_id: item.zone_id,
      subzone: item.subzone,
      warehouse_type: item.warehouse_type,
      transfers_enabled: item.transfers_enabled,
      delivery_user_id: item.delivery_user_id || "",
      responsible_user_id: item.responsible_user_id || "",
      coordinator_id: item.coordinator_id || null,
      is_main: Boolean(item.is_main),
      entries_enabled: Boolean(item.entries_enabled),
    });
    requestAnimationFrame(() => {
      document.querySelector('[data-testid="warehouse-create-panel"]')?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const toggle = async (item) => {
    try {
      await api.patch(`/warehouses/${item.warehouse_id}/status`, { active: !item.active });
      toast.success(item.active ? "Dépôt désactivé" : "Dépôt activé");
      load();
    } catch (requestError) {
      toast.error(getError(requestError));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer définitivement le dépôt « ${item.name} » ?`)) return;
    try {
      await api.delete(`/warehouses/${item.warehouse_id}`);
      toast.success("Dépôt supprimé");
      if (editing?.warehouse_id === item.warehouse_id) {
        setEditing(null);
        setWarehouse(emptyWarehouse);
      }
      load();
    } catch (requestError) {
      toast.error(getError(requestError));
    }
  };

  const selectedZone = zones.find((item) => item.zone_id === warehouse.zone_id);
  const isDeliveryWarehouse = warehouse.warehouse_type === "livreur";
  const filteredWarehouses = warehouses.filter((w) => w.name.toLowerCase().includes(query.toLowerCase()) && (!typeFilter || w.warehouse_type === typeFilter) && (!zoneFilter || w.zone_id === zoneFilter) && (!subzoneFilter || w.subzone === subzoneFilter));
  const scopedResponsibles = responsibles.filter((responsible) => {
    if (responsible.role === "coordinateur_zone") {
      return responsible.zone_id === warehouse.zone_id && responsible.subzone === warehouse.subzone;
    }
    if (responsible.role === "magasinier_principal" && responsible.zone_id) {
      return responsible.zone_id === warehouse.zone_id && (
        !responsible.subzone || responsible.subzone === warehouse.subzone
      );
    }
    return true;
  });

  return (
    <div className="page-enter" data-testid="locations-page">
      <div className="page-heading"><div><p className="eyebrow">MAILLE TERRITORIALE</p><h1>Dépôts & zones</h1><p>Créez, mettez à jour, activez ou désactivez les emplacements de votre réseau.</p></div></div>
      {error && <div className="notice-error" data-testid="locations-error-message">{error}</div>}
      <section className="warehouse-access-panel" data-testid="warehouse-quick-access-panel">
        <div className="warehouse-access-heading">
          <div>
            <p className="eyebrow">ACCÈS DIRECT</p>
            <h2>Ouvrir un dépôt</h2>
          </div>
          <span data-testid="warehouse-quick-access-count">{filteredWarehouses.length} dépôt(s)</span>
        </div>
        <div className="quick-search-bar warehouse-access-filters" data-testid="warehouses-quick-filters">
          <input
            aria-label="Rechercher un dépôt par nom"
            data-testid="warehouses-search-input"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher un dépôt"
            value={query}
          />
          <select
            aria-label="Filtrer les dépôts par type"
            data-testid="warehouses-type-filter"
            onChange={(event) => setTypeFilter(event.target.value)}
            value={typeFilter}
          >
            <option value="">Tous les types</option>
            {warehouseTypes.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            aria-label="Filtrer les dépôts par zone"
            data-testid="warehouses-zone-filter"
            onChange={(event) => {
              setZoneFilter(event.target.value);
              setSubzoneFilter("");
            }}
            value={zoneFilter}
          >
            <option value="">Toutes les zones</option>
            {zones.map((item) => (
              <option key={item.zone_id} value={item.zone_id}>{item.name}</option>
            ))}
          </select>
          <select
            aria-label="Filtrer les dépôts par sous-zone"
            data-testid="warehouses-subzone-filter"
            disabled={!zoneFilter}
            onChange={(event) => setSubzoneFilter(event.target.value)}
            value={subzoneFilter}
          >
            <option value="">Toutes les sous-zones</option>
            {zones.find((item) => item.zone_id === zoneFilter)?.subzones.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>
        <div className="warehouse-access-list">
          {filteredWarehouses.filter((item) => item.active).map((item) => (
            <button
              data-testid={`open-warehouse-${item.warehouse_id}`}
              key={item.warehouse_id}
              onClick={() => setSelectedWarehouseId(item.warehouse_id)}
              type="button"
            >
              <MapPin aria-hidden="true" size={16} />
              <span>
                <strong>{item.name}</strong>
                <small>{item.subzone} · {item.warehouse_type}</small>
                <small data-testid={`warehouse-responsible-${item.warehouse_id}`}>
                  {item.responsible_name ? `Responsable : ${item.responsible_name}` : "Responsable non affecté"}
                </small>
              </span>
            </button>
          ))}
          {!filteredWarehouses.filter((item) => item.active).length && (
            <p data-testid="warehouse-quick-access-empty">Aucun dépôt actif ne correspond aux filtres.</p>
          )}
        </div>
      </section>
      <div className="catalogue-layout">
        <section className="data-panel" data-testid="zone-create-panel">
          <div className="panel-heading"><div><p className="eyebrow">GÉOGRAPHIE</p><h2>Nouvelle zone</h2></div><Building2 size={19} /></div>
          <form className="stack-form" data-testid="zone-create-form" onSubmit={addZone}>
            <input data-testid="zone-name-input" onChange={(event) => setZone({ ...zone, name: event.target.value })} placeholder="Nom de la zone" required value={zone.name} />
            <input data-testid="zone-subzones-input" onChange={(event) => setZone({ ...zone, subzones: event.target.value })} placeholder="Sous-zones séparées par une virgule" value={zone.subzones} />
            <button className="secondary-action" data-testid="zone-create-button"><CirclePlus size={17} /> Ajouter la zone</button>
          </form>
        </section>
        <section className="data-panel" data-testid="warehouse-create-panel">
          <div className="panel-heading"><div><p className="eyebrow">EMPLACEMENT</p><h2>{editing ? `Modifier ${editing.name}` : "Nouveau dépôt"}</h2></div><Building2 size={19} /></div>
          <form className="stack-form" data-testid="warehouse-form" onSubmit={saveWarehouse}>
            <input data-testid="warehouse-name-input" onChange={(event) => setWarehouse({ ...warehouse, name: event.target.value })} placeholder="Nom du dépôt" required value={warehouse.name} />
            <select data-testid="warehouse-type-select" onChange={(event) => setWarehouse({ ...warehouse, warehouse_type: event.target.value, delivery_user_id: "" })} value={warehouse.warehouse_type}>{warehouseTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            <select data-testid="warehouse-zone-select" onChange={(event) => setWarehouse({ ...warehouse, zone_id: event.target.value, subzone: "", responsible_user_id: "" })} required value={warehouse.zone_id}><option value="">Choisir une zone</option>{zones.filter((item) => item.active).map((item) => <option key={item.zone_id} value={item.zone_id}>{item.name}</option>)}</select>
            <select data-testid="warehouse-subzone-select" disabled={!warehouse.zone_id} onChange={(event) => setWarehouse({ ...warehouse, subzone: event.target.value, responsible_user_id: "" })} required value={warehouse.subzone}><option value="">Choisir une sous-zone</option>{selectedZone?.subzones.map((item) => <option key={item} value={item}>{item}</option>)}</select>
            {isDeliveryWarehouse && warehouse.subzone && <select data-testid="warehouse-delivery-user-select" onChange={(event) => setWarehouse({ ...warehouse, delivery_user_id: event.target.value })} required value={warehouse.delivery_user_id}><option value="">Choisir le livreur rattaché</option>{users.filter((item) => item.role === "livreur" && item.zone_id === warehouse.zone_id && item.subzone === warehouse.subzone).map((item) => <option key={item.user_id} value={item.user_id}>{item.name}</option>)}</select>}
            {canAssignResponsible && (
              <div className="warehouse-form-responsible" data-testid="warehouse-form-responsible-field">
                <div className="warehouse-form-responsible-filters">
                  <label className="filter-search">
                    <Search aria-hidden="true" size={16} />
                    <input
                      data-testid="warehouse-form-responsible-search-input"
                      onChange={(event) => setResponsibleSearch(event.target.value)}
                      placeholder="Rechercher un responsable"
                      value={responsibleSearch}
                    />
                  </label>
                  <select
                    data-testid="warehouse-form-responsible-role-filter"
                    onChange={(event) => setResponsibleRole(event.target.value)}
                    value={responsibleRole}
                  >
                    <option value="">Tous les rôles</option>
                    <option value="magasinier_principal">Magasinier</option>
                    <option value="coordinateur_zone">Coordinateur de zone</option>
                    <option value="livreur">Livreur</option>
                  </select>
                </div>
                <label>
                  <span>Responsable principal</span>
                  <select
                    data-testid="warehouse-form-responsible-select"
                    disabled={!warehouse.zone_id || !warehouse.subzone}
                    onChange={(event) => setWarehouse({
                      ...warehouse,
                      responsible_user_id: event.target.value,
                    })}
                    value={warehouse.responsible_user_id}
                  >
                    <option value="">Aucun responsable affecté</option>
                    {scopedResponsibles.map((responsible) => (
                      <option key={responsible.user_id} value={responsible.user_id}>
                        {responsible.name} · {responsible.role.replace("_", " ")}
                      </option>
                    ))}
                  </select>
                </label>
                {!scopedResponsibles.length && warehouse.zone_id && warehouse.subzone && (
                  <small data-testid="warehouse-form-no-responsible-candidate">
                    Aucun responsable compatible avec cette zone et sous-zone.
                  </small>
                )}
              </div>
            )}
            <label className="check-label"><input checked={warehouse.transfers_enabled} data-testid="warehouse-transfers-enabled-input" onChange={(event) => setWarehouse({ ...warehouse, transfers_enabled: event.target.checked })} type="checkbox" /> Autoriser les transferts depuis ce dépôt</label>
            <div className="warehouse-form-actions"><button className="primary-action" data-testid="warehouse-save-button"><CirclePlus size={17} /> {editing ? "Enregistrer les modifications" : "Créer le dépôt"}</button>{editing && <button className="secondary-action" data-testid="cancel-warehouse-edit-button" onClick={() => { setEditing(null); setWarehouse(emptyWarehouse); }} type="button">Annuler</button>}</div>
          </form>
        </section>
      </div>
      <section className="locations-grid" data-testid="zones-and-warehouses-list">
        <div className="data-panel"><div className="panel-heading"><div><p className="eyebrow">ZONES</p><h2>Organisation</h2></div></div><div className="zone-list">{zones.map((item) => <div data-testid={`zone-item-${item.zone_id}`} key={item.zone_id}><strong>{item.name}</strong><span>{item.subzones.join(" · ") || "Aucune sous-zone"}</span></div>)}</div></div>
        <div className="data-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">DÉPÔTS</p>
              <h2>{warehouses.length} emplacement(s)</h2>
            </div>
          </div>
          <div className="warehouse-management-list scrollable-list">
            {filteredWarehouses.map((item) => (
              <div
                className={item.active ? "warehouse-management-row" : "warehouse-management-row inactive"}
                data-testid={`warehouse-item-${item.warehouse_id}`}
                key={item.warehouse_id}
              >
                <div>
                  <strong>{item.name}</strong>
                  <span>
                    {warehouseTypes.find(([value]) => value === item.warehouse_type)?.[1]}
                    {" · "}
                    {zones.find((zoneItem) => zoneItem.zone_id === item.zone_id)?.name}
                    {" · "}
                    {item.subzone}
                    {" · "}
                    <span data-testid={`warehouse-list-responsible-${item.warehouse_id}`}>
                      {item.responsible_name
                        ? `Responsable : ${item.responsible_name}`
                        : "Responsable non affecté"}
                    </span>
                  </span>
                </div>
                <div className="warehouse-row-actions">
                  <button
                    data-testid={`edit-warehouse-${item.warehouse_id}`}
                    onClick={() => startEdit(item)}
                    title="Modifier"
                    type="button"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`warehouse-status-${item.warehouse_id}`}
                    onClick={() => toggle(item)}
                    title={item.active ? "Désactiver" : "Activer"}
                    type="button"
                  >
                    <Power
                      className={item.active ? "text-green-600" : "text-zinc-400"}
                      size={16}
                    />
                  </button>
                  <button
                    data-testid={`delete-warehouse-${item.warehouse_id}`}
                    onClick={() => remove(item)}
                    title="Supprimer"
                    type="button"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {!warehouses.length && (
            <div className="empty-inline" data-testid="empty-warehouses-message">
              Ajoutez les dépôts qui composeront votre réseau.
            </div>
          )}
        </div>
      </section>
      <WarehouseDetailSheet
        onMovementOpen={(movementId) => setSelectedMovementId(movementId)}
        onOpenChange={(open) => !open && setSelectedWarehouseId("")}
        user={user}
        warehouseId={selectedWarehouseId}
      />
      <MovementDetailSheet
        movementId={selectedMovementId}
        onOpenChange={(open) => !open && setSelectedMovementId("")}
      />
    </div>
  );
}